import React, { useState, useEffect } from 'react';
import { listDevices, setPunchRoutes, listPunchQrs } from '../services/kioskService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { companiesService, type CompanySettings } from '../services/companiesService';

/**
 * 打刻設定。**どこから打刻できるか**という会社の運用方針なので、
 * 端末の管理画面ではなく企業設定に置く。
 *
 * 値は親（CompanySettingsPage）が持ち、**保存は他の設定と一緒に「保存」ボタンで行う**。
 * ここでは切り替えの可否と警告だけを受け持つ。
 *
 * ## 守っていること
 *
 * - **両方を無効にできない。** 誰も打刻できない会社ができてしまう
 * - **本人打刻を止めるには、ペアリング済みの端末が1台以上必要。**
 *   「共用端末が有効」だけでは打刻できる保証にならない（端末が0台なら
 *   設定上は経路があるのに現実には打てない）
 * - **QR未発行の従業員がいたら名前を挙げて警告する。** 発行漏れに気づかず
 *   止めると、その人はどこからも打刻できなくなる
 *
 * 検査はサーバ側にもある（routes/kioskAdmin.ts / DBのCHECK制約）。
 * ここで弾くのは、利用者に理由を見せるため。
 */
interface PunchSettingsProps {
  kioskEnabled: boolean;
  selfPunch: boolean;
  pairedCount: number;
  noQrNames: string[];
  onChange: (next: { kioskEnabled: boolean; selfPunch: boolean }) => void;
}

const PunchSettingsSection: React.FC<PunchSettingsProps> = ({
  kioskEnabled,
  selfPunch,
  pairedCount,
  noQrNames,
  onChange,
}) => {
  const toggleKiosk = () => onChange({ kioskEnabled: !kioskEnabled, selfPunch });

  const toggleSelfPunch = () => {
    if (selfPunch) {
      // 切り替えた時点で警告する。保存まで待つと、押した理由を忘れたころに出る
      const names = noQrNames.slice(0, 5).join('、');
      const more = noQrNames.length > 5 ? ` ほか${noQrNames.length - 5}名` : '';
      const qrWarning =
        noQrNames.length > 0
          ? `\n\n⚠️ QRコードが未発行の従業員が ${noQrNames.length} 名います。\n` +
            `　 ${names}${more}\n` +
            `　 このまま無効にすると、この方々は打刻できなくなります。`
          : '';
      const ok = window.confirm(
        '本人の画面（ダッシュボード）からの打刻を無効にします。\n\n' +
        '・以後、従業員は共用端末でしか打刻できません\n' +
        '・打刻修正申請は引き続き使えます（打ち損ねたときの回復手段）' +
        qrWarning +
        '\n\n（この変更は「保存」を押した時点で反映されます）\n\n続けますか？'
      );
      if (!ok) return;
    }
    onChange({ kioskEnabled, selfPunch: !selfPunch });
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '16px 18px',
    borderTop: '1px solid #dee2e6',
    marginTop: -1,
    backgroundColor: 'white',
  };
  const toggleStyle = (on: boolean, disabled: boolean): React.CSSProperties => ({
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    padding: '10px 28px',
    fontSize: 15,
    fontWeight: 700,
    backgroundColor: on ? '#2ea44f' : '#6c757d',
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    flex: '0 0 auto',
  });
  const noteStyle: React.CSSProperties = { color: '#6c757d', fontSize: 13, lineHeight: 1.7, margin: '4px 0 0' };

  // 最後の1経路は消せない。本人打刻を止めるには実際に打てる端末が要る
  const kioskLocked = kioskEnabled && !selfPunch;
  const selfLocked = selfPunch && (!kioskEnabled || pairedCount === 0);

  return (
    <section>
      <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
        打刻設定
      </h2>

      <p style={noteStyle}>
        どこから打刻できるかを決めます。
        <strong>両方を無効にはできません</strong>（誰も打刻できなくなるため）。
      </p>

      <div style={{ marginTop: 12, border: '1px solid #dee2e6', borderRadius: 6, overflow: 'hidden' }}>
        <div style={rowStyle}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#24292e', marginBottom: 4 }}>
              共用端末からの打刻
            </div>
            <p style={noteStyle}>
              店舗に置いた端末でQRをかざして打刻します。
              {pairedCount === 0
                ? '登録済みの端末はまだありません（有効でも、端末が無ければ打刻は始まりません）。'
                : `登録済みの端末: ${pairedCount}台。`}
              無効にすると、登録済みの端末からも打刻できなくなります。
            </p>
          </div>
          <button type="button" onClick={toggleKiosk} disabled={kioskLocked} style={toggleStyle(kioskEnabled, kioskLocked)}>
            {kioskEnabled ? '有効' : '無効'}
          </button>
        </div>

        <div style={rowStyle}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#24292e', marginBottom: 4 }}>
              本人の画面からの打刻
            </div>
            <p style={noteStyle}>
              ダッシュボードの打刻ボタンです。無効にすると
              <strong>共用端末でしか打刻できなくなります</strong>
              （その場にいたことを担保したい運用向け）。打刻修正申請は引き続き使えます。
            </p>
            {selfPunch && pairedCount === 0 && (
              <p style={{ ...noteStyle, color: '#B45309' }}>
                登録済みの端末が無いため無効にできません。先に「打刻用共用端末」で端末を登録してください。
              </p>
            )}
            {selfPunch && noQrNames.length > 0 && pairedCount > 0 && (
              <p style={{ ...noteStyle, color: '#B45309' }}>
                ⚠️ QRコードが未発行の従業員が {noQrNames.length} 名います。
                無効にする前に発行してください。
              </p>
            )}
          </div>
          <button type="button" onClick={toggleSelfPunch} disabled={selfLocked} style={toggleStyle(selfPunch, selfLocked)}>
            {selfPunch ? '有効' : '無効'}
          </button>
        </div>
      </div>
    </section>
  );
};

const CompanySettingsPage: React.FC = () => {
  // 打刻設定。他の設定と同じく「保存」でまとめて反映する
  const [kioskEnabled, setKioskEnabled] = useState(true);
  const [selfPunch, setSelfPunch] = useState(true);
  const [pairedCount, setPairedCount] = useState(0);
  const [noQrNames, setNoQrNames] = useState<string[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // フォームの状態
  const [companyName, setCompanyName] = useState('');
  const [autoBreakDeductionEnabled, setAutoBreakDeductionEnabled] = useState(false);
  const [breakRules, setBreakRules] = useState<{ workHours: number; breakMinutes: number }[]>([]);
  const [standardWorkStartTime, setStandardWorkStartTime] = useState('');
  const [standardWorkEndTime, setStandardWorkEndTime] = useState('');
  const [standardWorkHours, setStandardWorkHours] = useState(8);
  const [nightShiftStart, setNightShiftStart] = useState('22:00');
  const [nightShiftEnd, setNightShiftEnd] = useState('05:00');
  const [holidayMonday, setHolidayMonday] = useState(false);
  const [holidayTuesday, setHolidayTuesday] = useState(false);
  const [holidayWednesday, setHolidayWednesday] = useState(false);
  const [holidayThursday, setHolidayThursday] = useState(false);
  const [holidayFriday, setHolidayFriday] = useState(false);
  const [holidaySaturday, setHolidaySaturday] = useState(true);
  const [holidaySunday, setHolidaySunday] = useState(true);
  const [holidayNationalHolidays, setHolidayNationalHolidays] = useState(true);

  // 打刻設定の現在値と、判断に要る情報（端末数・QR未発行者）を読む
  useEffect(() => {
    Promise.all([listDevices(), listPunchQrs()])
      .then(([d, qrs]) => {
        setKioskEnabled(d.kioskEnabled);
        setSelfPunch(d.selfPunchEnabled);
        setPairedCount(d.devices.filter((x) => !x.revoked_at && x.paired).length);
        setNoQrNames(qrs.filter((q) => !q.qrToken).map((q) => q.name));
      })
      .catch(() => { /* 読めなくても他の設定は編集できるようにする */ });
  }, []);

  useEffect(() => {
    if (user?.role !== 'master') {
      navigate('/dashboard');
      return;
    }

    loadSettings();
  }, [user, navigate]);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await companiesService.getSettings();
      setSettings(data);

      // フォームに値をセット
      setCompanyName(data.company_name);
      setAutoBreakDeductionEnabled(data.auto_break_deduction_enabled);
      setBreakRules(data.break_rules || []);
      setStandardWorkStartTime(data.standard_work_start_time || '');
      setStandardWorkEndTime(data.standard_work_end_time || '');
      // 数値として確実に設定（文字列の場合も変換）
      const workHours = typeof data.standard_work_hours === 'string'
        ? parseFloat(data.standard_work_hours)
        : data.standard_work_hours;
      setStandardWorkHours(isNaN(workHours) ? 8 : workHours);
      // 休日設定
      setHolidayMonday(data.holiday_monday || false);
      setHolidayTuesday(data.holiday_tuesday || false);
      setHolidayWednesday(data.holiday_wednesday || false);
      setHolidayThursday(data.holiday_thursday || false);
      setHolidayFriday(data.holiday_friday || false);
      setHolidaySaturday(data.holiday_saturday !== undefined ? data.holiday_saturday : true);
      setHolidaySunday(data.holiday_sunday !== undefined ? data.holiday_sunday : true);
      setHolidayNationalHolidays(data.holiday_national_holidays !== undefined ? data.holiday_national_holidays : true);
      // 深夜時間帯設定
      setNightShiftStart(data.night_shift_start ? data.night_shift_start.substring(0, 5) : '22:00');
      setNightShiftEnd(data.night_shift_end ? data.night_shift_end.substring(0, 5) : '05:00');
    } catch (err: any) {
      setError(err.response?.data?.error || '企業設定の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBreakRule = () => {
    setBreakRules([...breakRules, { workHours: 0, breakMinutes: 0 }]);
  };

  const handleRemoveBreakRule = (index: number) => {
    setBreakRules(breakRules.filter((_, i) => i !== index));
  };

  const handleBreakRuleChange = (index: number, field: 'workHours' | 'breakMinutes', value: number) => {
    const newRules = [...breakRules];
    newRules[index][field] = value;
    setBreakRules(newRules);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');

    // バリデーション
    if (!companyName.trim()) {
      setError('企業名を入力してください');
      return;
    }

    if (standardWorkHours <= 0 || standardWorkHours > 24) {
      setError('所定労働時間は0〜24時間の範囲で入力してください');
      return;
    }

    // 休憩ルールのバリデーション
    for (const rule of breakRules) {
      if (rule.workHours <= 0 || rule.breakMinutes <= 0) {
        setError('休憩ルールの値は0より大きい必要があります');
        return;
      }
    }

    try {
      setIsSaving(true);

      const data: Partial<CompanySettings> = {
        company_name: companyName.trim(),
        auto_break_deduction_enabled: autoBreakDeductionEnabled,
        break_rules: breakRules,
        standard_work_start_time: null,
        standard_work_end_time: null,
        standard_work_hours: standardWorkHours,
        night_shift_start: nightShiftStart + ':00',
        night_shift_end: nightShiftEnd + ':00',
        holiday_monday: holidayMonday,
        holiday_tuesday: holidayTuesday,
        holiday_wednesday: holidayWednesday,
        holiday_thursday: holidayThursday,
        holiday_friday: holidayFriday,
        holiday_saturday: holidaySaturday,
        holiday_sunday: holidaySunday,
        holiday_national_holidays: holidayNationalHolidays
      };

      await companiesService.updateSettings(data);

      // 打刻設定は別のエンドポイント（打刻経路の検査を持つため）。
      // **企業設定の保存が成功してから**送る。先に送ると、企業設定が
      // 失敗したときに打刻設定だけ変わってしまう
      await setPunchRoutes({ enabled: kioskEnabled, selfPunchEnabled: selfPunch });

      setMessage('企業設定を更新しました');

      // 3秒後にメッセージを消す
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '企業設定の更新に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>読み込み中...</div>;
  }

  if (!settings) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>企業設定が見つかりません</div>;
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' }}>
      <header style={{ marginBottom: 'clamp(15px, 4vw, 30px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
            <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>企業設定</h1>
            {user && (
              <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                {user.name}さん ({user.companyCode})
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              whiteSpace: 'nowrap'
            }}
          >
            ダッシュボードに戻る
          </button>
        </div>
      </header>

      <form onSubmit={handleSubmit} style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        {/* 企業基本情報 */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
            企業基本情報
          </h2>

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="companyName" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
              企業名 <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              id="companyName"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="companyCode" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
              企業コード（変更不可）
            </label>
            <input
              id="companyCode"
              type="text"
              value={settings.company_code}
              disabled
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#e9ecef',
                cursor: 'not-allowed'
              }}
            />
          </div>

        </section>

        {/* 基本勤務時間設定 */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
            基本勤務時間設定
          </h2>

          <div>
            <label htmlFor="workHours" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
              所定労働時間（時間） <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              id="workHours"
              type="number"
              min="0.5"
              max="24"
              step="0.5"
              value={standardWorkHours}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setStandardWorkHours(isNaN(val) ? 8 : val);
              }}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '8px 12px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              required
            />
            <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
            デフォルト: 8
            </p>
          </div>

          <p style={{ marginTop: '10px', fontSize: '13px', color: '#6c757d' }}>
          休憩時間を除く1日の所定労働時間を設定します。この時間を超えた分は時間外労働時間として計算されます。
          </p>
        </section>

        {/* 深夜時間帯設定 */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
            深夜時間帯設定
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', maxWidth: '600px' }}>
            <div>
              <label htmlFor="nightShiftStart" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                深夜開始時刻 <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                id="nightShiftStart"
                type="time"
                value={nightShiftStart}
                onChange={(e) => setNightShiftStart(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                required
              />
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
                デフォルト: 22:00
              </p>
            </div>

            <div>
              <label htmlFor="nightShiftEnd" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                深夜終了時刻 <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                id="nightShiftEnd"
                type="time"
                value={nightShiftEnd}
                onChange={(e) => setNightShiftEnd(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                required
              />
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
                デフォルト: 05:00
              </p>
            </div>
          </div>

          <p style={{ marginTop: '10px', fontSize: '13px', color: '#6c757d' }}>
            深夜労働の時間帯を設定します。深夜時間帯に勤務した時間が自動的に計算されます。
          </p>
        </section>

        {/* 休憩時間自動控除設定 */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
            休憩時間自動控除設定
          </h2>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={autoBreakDeductionEnabled}
                onChange={(e) => setAutoBreakDeductionEnabled(e.target.checked)}
                style={{ marginRight: '8px', cursor: 'pointer', width: '18px', height: '18px' }}
              />
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>休憩時間自動控除を有効にする</span>
            </label>
            <p style={{ margin: '5px 0 0 26px', fontSize: '12px', color: '#6c757d' }}>
              有効にすると、勤務時間に応じて自動的に休憩時間が控除されます。従業員は手動で休憩打刻をする必要がなくなります。
            </p>
          </div>

          {autoBreakDeductionEnabled && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontWeight: 'bold', fontSize: '14px' }}>休憩ルール</label>
                <button
                  type="button"
                  onClick={handleAddBreakRule}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  + ルール追加
                </button>
              </div>

              {breakRules.length === 0 ? (
                <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px', textAlign: 'center', color: '#6c757d' }}>
                  休憩ルールが設定されていません。「+ ルール追加」ボタンでルールを追加してください。
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {breakRules.map((rule, index) => (
                    <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>勤務時間（時間以上）</label>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={rule.workHours}
                          onChange={(e) => handleBreakRuleChange(index, 'workHours', parseFloat(e.target.value))}
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>休憩時間（分）</label>
                        <input
                          type="number"
                          min="0"
                          step="5"
                          value={rule.breakMinutes}
                          onChange={(e) => handleBreakRuleChange(index, 'breakMinutes', parseInt(e.target.value))}
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveBreakRule(index)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginTop: '18px'
                        }}
                      >
                        削除
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
                例：6時間以上勤務した場合は45分、8時間以上勤務した場合は60分の休憩を自動控除
              </p>
            </div>
          )}
        </section>

        {/* 休日設定 */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
            休日設定
          </h2>
          <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#6c757d' }}>
            休日として扱う曜日と国民の祝日を設定します。休日に勤務した場合は、休日出勤として扱われ、労働時間の集計が区別されます。
          </p>

          {/* 曜日設定 */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', marginBottom: '10px', color: '#555' }}>曜日設定</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={holidayMonday}
                  onChange={(e) => setHolidayMonday(e.target.checked)}
                  style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px' }}>月曜日</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={holidayTuesday}
                  onChange={(e) => setHolidayTuesday(e.target.checked)}
                  style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px' }}>火曜日</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={holidayWednesday}
                  onChange={(e) => setHolidayWednesday(e.target.checked)}
                  style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px' }}>水曜日</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={holidayThursday}
                  onChange={(e) => setHolidayThursday(e.target.checked)}
                  style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px' }}>木曜日</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={holidayFriday}
                  onChange={(e) => setHolidayFriday(e.target.checked)}
                  style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px' }}>金曜日</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={holidaySaturday}
                  onChange={(e) => setHolidaySaturday(e.target.checked)}
                  style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px' }}>土曜日</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <input
                  type="checkbox"
                  checked={holidaySunday}
                  onChange={(e) => setHolidaySunday(e.target.checked)}
                  style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '14px' }}>日曜日</span>
              </label>
            </div>
          </div>

          {/* 祝日設定 */}
          <div>
            <h3 style={{ fontSize: '16px', marginBottom: '10px', color: '#555' }}>祝日設定</h3>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', maxWidth: '300px' }}>
              <input
                type="checkbox"
                checked={holidayNationalHolidays}
                onChange={(e) => setHolidayNationalHolidays(e.target.checked)}
                style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
              />
              <span style={{ fontSize: '14px' }}>国民の祝日を休日とする</span>
            </label>
            <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
              日本の法定祝日（元日、成人の日、建国記念の日など）が自動的に休日として扱われます。
            </p>
          </div>
        </section>

        {/* メッセージ表示エリア */}
        {error && (
          <div style={{
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '4px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {message && (
          <div style={{
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '4px',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb'
          }}>
            {message}
          </div>
        )}

        {/*
          保存・キャンセルは**カードの枠外**に置く。
          設定項目の中に混ざっていると、どこまでが入力でどこからが操作か
          分かりにくい。枠の外に出すと「このカード全体を保存する」ことが
          形として読み取れる。
        */}
      </form>

      {/*
        打刻設定は独立したカードにする。他の設定と性質が違い
        （数値の調整ではなく「打刻できる/できない」の切り替え）、
        誤って触ると現場が止まるため、視覚的にも分けておく。
        保存は下のボタンで、他の設定とまとめて行う。
      */}
      <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginTop: '20px' }}>
        <PunchSettingsSection
          kioskEnabled={kioskEnabled}
          selfPunch={selfPunch}
          pairedCount={pairedCount}
          noQrNames={noQrNames}
          onChange={(next) => {
            setKioskEnabled(next.kioskEnabled);
            setSelfPunch(next.selfPunch);
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '20px' }}>
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '10px 24px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          キャンセル
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSaving}
          style={{
            padding: '10px 24px',
            backgroundColor: isSaving ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          {isSaving ? '保存中...' : '保存'}
        </button>
      </div>
    </div>
  );
};

export default CompanySettingsPage;
